import classnames from 'classnames'
import { css } from 'emotion'
import { get } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import { fetchCityStateFromZip, fetchZipcodeFromIP, stepSelected, updateLead } from 'common/actions'
import {
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldRegistered,
} from 'common/actions/application'
import { Field, FieldDataValue, PublicClassNames, Waypoints } from 'embed/constants'
import { Fields } from 'embed/constants/fields'
import { CreditOffer } from 'embed/constants/models'

import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import { ApplicationField } from 'embed/components/ApplicationField/container'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton/container'
import { brandSelector, configStepSelector } from 'embed/selectors/config'
import { MultiProductState } from 'embed/reducers'
import { Brand, ConfigKeys, CreditCardStep, Filter, OfferType, Product } from 'even-ts-static'
import Waypoint from '../../Waypoint'
import { useTheme } from 'embed/hooks'

const styles = require('embed/styles/application')

interface StoreProps {
  brand: Brand
  configuredForIntroStep: boolean
  consent: boolean
  consentValidationStatus: boolean
  directReferral: boolean
  hasCalledGeocoder: boolean
  selectedOffer?: CreditOffer
  zipcode?: FieldDataValue
}

interface DispatchProps {
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: FieldDataValue) => void
  dispatchApplicationFieldRegistered: (field: Field) => void
  dispatchBackToIntroStep: () => void
  dispatchBackToStepOne: () => void
  dispatchFetchCityStateFromZip: (zipcode: FieldDataValue) => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchUpdateLead: () => void
}

type Props = DispatchProps & StoreProps

const CreditCardRemarketingFormView: React.FC<Props> = ({
  configuredForIntroStep,
  consent,
  consentValidationStatus,
  dispatchFetchZipcodeFromIP,
  dispatchFetchCityStateFromZip,
  dispatchApplicationFieldRegistered,
  dispatchUpdateLead,
  dispatchApplicationFieldBlurred,
  dispatchApplicationFieldChanged,
  dispatchBackToIntroStep,
  dispatchBackToStepOne,
  directReferral,
  hasCalledGeocoder,
  selectedOffer,
  zipcode,
}) => {
  const {
    backdropStyles,
    borderRadiusLarge,
    boxStyles,
    colorBorder,
    ctaStyles,
    errorStyles,
    fontFamilyPrimary,
  } = useTheme()

  React.useEffect(() => {
    if (hasCalledGeocoder === false) {
      dispatchFetchZipcodeFromIP()
    } else if (zipcode !== undefined) {
      dispatchFetchCityStateFromZip(zipcode)
    }
  }, [])

  React.useEffect(() => {
    dispatchUpdateLead()
    dispatchApplicationFieldRegistered(Fields.ConsentsToFCRA)
    dispatchApplicationFieldRegistered(Fields.ConsentsToTCPA)
  }, [])

  const onConsentChange = React.useCallback(
    (event) => {
      dispatchApplicationFieldChanged(Fields.ConsentsToFCRA, event.target.checked)
      dispatchApplicationFieldBlurred(Fields.ConsentsToFCRA)
      dispatchApplicationFieldChanged(Fields.ConsentsToTCPA, event.target.checked)
      dispatchApplicationFieldBlurred(Fields.ConsentsToTCPA)
    },
    [dispatchApplicationFieldChanged, dispatchApplicationFieldBlurred],
  )

  const handleReturnToOriginalStep = configuredForIntroStep ? dispatchBackToIntroStep : dispatchBackToStepOne

  const checkboxBorder =
    !consent && consentValidationStatus
      ? `border: 1px solid ${errorStyles?.color} !important;`
      : `border-color: #323232;`
  return (
    <React.Fragment>
      <div
        className={classnames(
          PublicClassNames.ApplicationContainer,
          styles.container,
          css`
            background-color: ${backdropStyles.backgroundColor};
            border-color: ${colorBorder};
            border-radius: ${borderRadiusLarge};
          `,
        )}
        style={boxStyles}
      >
        {!directReferral && <ReturnToSearch className={styles.backToSearch} onClick={handleReturnToOriginalStep} />}
        <h4 className={styles.noAffect}>This service is free and will NOT affect your credit score</h4>
        <section className={styles.formSection} data-test-id="form_remarketing">
          <h2
            className={classnames(
              styles.heading,
              css`
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            Your Information
          </h2>
          <ApplicationField product={Product.CreditCard} field={Fields.FirstName} />
          <ApplicationField product={Product.CreditCard} field={Fields.LastName} />
          <ApplicationField product={Product.CreditCard} field={Fields.EmailAddress} />
          <ApplicationField product={Product.CreditCard} field={Fields.AddressOne} />
          <ApplicationField product={Product.CreditCard} field={Fields.AddressTwo} />
          <ApplicationField product={Product.CreditCard} field={Fields.Zipcode} />
          <ApplicationField
            product={Product.CreditCard}
            field={Fields.City}
            styles={css`
              width: calc(30% - (1rem * 2));
            `}
          />
          <ApplicationField
            product={Product.CreditCard}
            field={Fields.State}
            styles={css`
              width: calc(20% - (1rem * 2));
            `}
          />
          <Waypoint name={Waypoints.YourInfo} />
        </section>

        {/* These are hidden fields which need to be here so they register themselves with the form
                  and are included in validation of the application */}
        <ApplicationField product={Product.CreditCard} field={Fields.ConsentsToFCRA} />
        <ApplicationField product={Product.CreditCard} field={Fields.ConsentsToTCPA} />
        <div className={classnames(styles.consentFieldContainer)}>
          <TCPALanguage buttonText="Continue to Apply" product={Product.CreditCard}>
            <label className={styles.consentCheckboxLabel}>
              <input
                type="checkbox"
                onChange={onConsentChange}
                name="consent"
                checked={consent}
                className={classnames(
                  styles.consentCheckbox,
                  css`
                    ${checkboxBorder}
                    background-color: #fff;
                    &:checked {
                      border-color: #323232;
                      background-color: ${ctaStyles?.backgroundColor};
                    }
                  `,
                )}
              />
              <span />
            </label>
          </TCPALanguage>
        </div>
        <Waypoint name={Waypoints.SubmitBtn} />
        <ApplicationSubmitButton product={Product.CreditCard} offer={selectedOffer} offerType={OfferType.CreditCard}>
          Continue to Apply
        </ApplicationSubmitButton>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = (state: MultiProductState): StoreProps => {
  const configStep = configStepSelector(state)
  const configuredForIntroStep = configStep === CreditCardStep.Intro
  return {
    brand: brandSelector(state),
    configuredForIntroStep,
    consent:
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToFCRA]?.value &&
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToTCPA]?.value,
    consentValidationStatus:
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToFCRA]?.shouldShowValidationStatus ||
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToTCPA]?.shouldShowValidationStatus,
    directReferral: state.config[ConfigKeys.Step] === CreditCardStep.RemarketingForm,
    hasCalledGeocoder: state.ui.hasCalledGeocoder,
    selectedOffer: state.rates.selected[Product.CreditCard],
    zipcode: state.application[Product.CreditCard]?.fields?.[Filter.Zipcode]?.value,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchApplicationFieldBlurred: (field: Field) => dispatch(applicationFieldBlurred(Product.CreditCard, field.key)),
  dispatchApplicationFieldChanged: (field: Field, value) =>
    dispatch(applicationFieldChanged(Product.CreditCard, field.key, value)),
  dispatchApplicationFieldRegistered: (field: Field) => dispatch(applicationFieldRegistered(Product.Loan, field.key)),
  dispatchBackToIntroStep: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Intro)),
  dispatchBackToStepOne: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Preview)),
  dispatchFetchCityStateFromZip: (zipcode) => dispatch(fetchCityStateFromZip(Product.CreditCard, zipcode)),
  dispatchFetchZipcodeFromIP: () => dispatch(fetchZipcodeFromIP(Product.CreditCard)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.CreditCard)),
})

export const CreditCardRemarketingForm = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(CreditCardRemarketingFormView)
